import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGenerator from './../../components/FormGenerator/FormGenerator';
import {
    getOrganizationalInformation,
    getUserInformation,
    saveOnboarding
} from "../../library/api/onboarding";
import onboardform from "../../features/onboard/onboard.json";
import useShowPopup from "../../components/SimplePopUp";
import {useTranslation} from "react-i18next";

export default function SettingByOnboarding({defaultState = 0}) {
    const { t } = useTranslation();

    const showPopup = useShowPopup(); // <-- call the hook

    const LOCKS = {
        organizationInformation: new Set(["companyId"]),
        userInformation: new Set([]),
    };
    const HIDDEN = {
        organizationInformation: new Set(["hasSubsidiaries"]), // hide this input
        userInformation: new Set([]),
    };
    const forms = onboardform;
    const section = forms[defaultState];
    const activeSectionId = section?.id;

    const [formData, setFormData] = React.useState({});
    const [invalidBySection, setInvalidBySection] = React.useState({});
    const [loaded, setLoaded] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const reportValidity = React.useCallback((sectionId, keyPath, isValid, message) => {
        setInvalidBySection(prev => {
            const sec = prev[sectionId] || {};
            const nextSec = {...sec, [keyPath]: isValid ? null : (message || 'Required')};
            return {...prev, [sectionId]: nextSec};
        });
    }, []);

    const sectionHasErrors = React.useCallback((sectionId) => {
        const sec = invalidBySection[sectionId];
        return !!sec && Object.values(sec).some(v => v);
    }, [invalidBySection]);

    const fetchInitialData = React.useCallback(async () => {
        try {
            const initial = forms.reduce((acc, f) => ({...acc, [f.id]: {}}), {});
            const orgInfo = await getOrganizationalInformation();
            if (orgInfo) initial["organizationInformation"] = orgInfo["organizationInformation"];
            const userInfo = await getUserInformation();
            if (userInfo) initial["userInformation"] = userInfo["userInformation"];
            setFormData(initial);
        } catch (e) {
            console.error("Error initializing form data:", e);
            showPopup.error(e?.message)
        } finally {
            setLoaded(true);
        }
    }, [forms]);

    React.useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleChange = (fieldId, value, parentSection) => {
        setFormData(prev => ({
            ...prev,
            [parentSection]: {
                ...(prev[parentSection] || {}),
                [fieldId]: value,
            },
        }));
    };

    const handleSave = async () => {
        if (!activeSectionId) return;
        try {
            setSaving(true);
            await saveOnboarding(defaultState, formData[activeSectionId]);
        } catch (e) {
            console.error(e);
            showPopup.error(e?.message)
        } finally {
            setSaving(false);
            showPopup.success()
        }
    };

    if (!section) return null;

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                            <h4 className="mb-sm-0">{t(section.label)}</h4>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <Box>
                                    {loaded && (
                                        <FormGenerator
                                            parentSection={activeSectionId}
                                            fields={section.fields}
                                            values={formData}
                                            onChange={handleChange}
                                            reportValidity={reportValidity}
                                            disabledMap={LOCKS}
                                            hiddenMap={HIDDEN}
                                            info={"organizationInformation.hasSubsidiaries.info1"}
                                        />
                                    )}
                                </Box>

                                <Box sx={{display: 'flex', justifyContent: 'flex-end', pt: 4, gap: 2}}>
                                    <Button
                                        onClick={handleSave}
                                        variant="contained"
                                        sx={{borderRadius: '50px'}}
                                        disabled={saving || sectionHasErrors(activeSectionId)}
                                    >
                                        Save changes
                                    </Button>
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
